import { Tooltip, Switch } from 'antd';
import Icon, { PlusCircleFilled } from '@ant-design/icons';
import { RestartCircleIconSvg } from '@/assets/svg';
import PauseCircleSvg from '@/assets/svg/PauseCircleIconSvg';
import PlayCircleSvg from '@/assets/svg/PlayCircleIconSvg';
import dayjs from 'dayjs';
import { Parameter, Iteration } from '@/pages/InteractiveSimulation/types/replayInteractive';
import { usePlayerChart } from '@/components/General/PlayerChart';
import { interactiveSimulationsEventsService } from '@/events/interactiveSimulations';

type ChartMode = 'live' | 'replay';

interface InteractiveChartHeaderProps {
	title: string;
	parameters: Parameter[];
	iterations: Iteration[];
	currentIteration: Iteration | null;
	switchEnabled: boolean;
	onSwitchChange: (checked: boolean) => void;
	mode: ChartMode;

	currentIterationCompleted?: boolean;
	isWaitingResponse?: boolean;
	onCreateNewIteration?: () => Promise<void>;
	isLivePlaying?: boolean;
	onLivePlay?: () => void;
	onLivePause?: () => void;
}

function InteractiveChartHeader(props: InteractiveChartHeaderProps) {
	const {
		title,
		parameters,
		iterations,
		currentIteration,
		switchEnabled,
		onSwitchChange,
		mode,
		currentIterationCompleted = false,
		isWaitingResponse = false,
		onCreateNewIteration,
		isLivePlaying = false,
		onLivePlay,
		onLivePause,
	} = props;

	const {
		isPlaying: isReplayPlaying,
		currentIndex,
		totalTimestamps,
		maxTimestamp,
		minTimestamp,
		handlePlay: handleReplayPlay,
		handleStop: handleReplayStop,
		handleReset: handleReplayReset,
	} = usePlayerChart();

	const handleIterationClick = (iteration: Iteration) => {
		interactiveSimulationsEventsService.setSubject({
			type: 'setCurrentIteration',
			data: iteration,
		});
	};

	const hasParameters = parameters && parameters.length > 0;
	const isIterationNavigationDisabled = mode === 'live' && !currentIterationCompleted;

	const isPlaying = mode === 'live' ? isLivePlaying : isReplayPlaying;
	const isAtEnd = mode === 'replay' && currentIndex >= totalTimestamps - 1;

	const renderControls = () => {
		if (mode === 'live') {
			if (currentIterationCompleted) {
				return (
					<Tooltip title={isWaitingResponse ? 'Waiting for last message' : 'Start New Iteration'}>
						<PlusCircleFilled
							className={isWaitingResponse ? 'cursor-not-allowed' : 'cursor-pointer'}
							style={{
								color: isWaitingResponse ? '#a0a0a0' : '#22A2CA',
								width: '30px',
								height: '30px',
								fontSize: '30px',
								opacity: isWaitingResponse ? 0.6 : 1,
							}}
							onClick={() => {
								if (!isWaitingResponse && onCreateNewIteration) {
									void onCreateNewIteration();
								}
							}}
						/>
					</Tooltip>
				);
			}
			return (
				<>
					<Tooltip
						title={
							isWaitingResponse
								? 'Waiting for response'
								: !hasParameters
									? 'Select parameters first'
									: isPlaying
										? 'Pause'
										: 'Play'
						}
					>
						<Icon
							component={isPlaying ? PauseCircleSvg : PlayCircleSvg}
							className="control-button"
							style={{
								width: '28px',
								height: '28px',
								fontSize: '28px',
								color: isWaitingResponse ? '#a0a0a0' : !hasParameters ? '#d1d5db' : '#22c55e',
								cursor: isWaitingResponse || !hasParameters ? 'not-allowed' : 'pointer',
								opacity: isWaitingResponse || !hasParameters ? 0.5 : 1,
							}}
							onClick={() => {
								if (!isWaitingResponse && hasParameters) {
									if (isPlaying && onLivePause) {
										onLivePause();
									} else if (!isPlaying && onLivePlay) {
										onLivePlay();
									}
								}
							}}
						/>
					</Tooltip>

					{isWaitingResponse && (
						<span className="waiting-message text-xs text-gray-600 ml-2">
							Retrieving last message, please wait.
						</span>
					)}
				</>
			);
		}

		if (!isAtEnd) {
			return (
				<>
					<Tooltip
						title={!hasParameters ? 'Select parameters first' : isPlaying ? 'Pause' : 'Play'}
					>
						<Icon
							component={isPlaying ? PauseCircleSvg : PlayCircleSvg}
							className="control-button"
							style={{
								width: '28px',
								height: '28px',
								fontSize: '28px',
								color: !hasParameters ? '#d1d5db' : '#22c55e',
								cursor: !hasParameters ? 'not-allowed' : 'pointer',
								opacity: !hasParameters ? 0.5 : 1,
							}}
							onClick={
								hasParameters ? (isPlaying ? handleReplayStop : handleReplayPlay) : undefined
							}
						/>
					</Tooltip>

					{isPlaying && hasParameters && (
						<Tooltip title="Reset to start">
							<Icon
								component={RestartCircleIconSvg}
								className="control-button"
								style={{
									width: '28px',
									height: '28px',
									fontSize: '28px',
									color: '#3b82f6',
									cursor: 'pointer',
								}}
								onClick={handleReplayReset}
							/>
						</Tooltip>
					)}
				</>
			);
		}

		return (
			<>
				{hasParameters ? (
					<Tooltip title="Replay">
						<Icon
							component={RestartCircleIconSvg}
							className="control-button"
							style={{
								width: '28px',
								height: '28px',
								fontSize: '28px',
								color: '#3b82f6',
								cursor: 'pointer',
							}}
							onClick={handleReplayReset}
						/>
					</Tooltip>
				) : (
					<Tooltip title="Select parameters first">
						<Icon
							component={PlayCircleSvg}
							className="control-button"
							style={{
								width: '28px',
								height: '28px',
								fontSize: '28px',
								color: '#d1d5db',
								cursor: 'not-allowed',
								opacity: 0.5,
							}}
						/>
					</Tooltip>
				)}
			</>
		);
	};

	return (
		<div className="relative flex items-center justify-between w-full gap-2">
			<div className="flex items-center gap-3 flex-shrink-0">
				<h3 className="text-base font-medium text-gray-800 whitespace-nowrap">{title}</h3>
				{iterations && iterations.length > 0 && (
					<div className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 rounded-lg border border-gray-200">
						{iterations.map((iteration) => (
							<button
								key={`iteration-${iteration.iterationId}`}
								type="button"
								disabled={isIterationNavigationDisabled}
								onClick={() => {
									if (!isIterationNavigationDisabled) {
										handleIterationClick(iteration);
									}
								}}
								className={`
									px-2 py-0.5 text-xs font-medium rounded transition-all duration-200
									${
										currentIteration && currentIteration.iterationId === iteration.iterationId
											? 'bg-cyan-500 text-white shadow-sm'
											: isIterationNavigationDisabled
												? 'text-gray-300 cursor-not-allowed'
												: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
									}
								`}
								title={isIterationNavigationDisabled ? 'Complete the current iteration before switching' : undefined}
							>
								Iteration {iteration.iterationId}
							</button>
						))}
					</div>
				)}
			</div>

			<div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2 z-10">
				{renderControls()}
			</div>

			<div className="flex items-center gap-3 flex-shrink-0">
				<div className="text-xs text-gray-600 text-right">
					<div className="font-medium">
						From: {dayjs(minTimestamp).format('YYYY-MM-DD HH:mm A')}
					</div>
					<div className="font-medium">To: {dayjs(maxTimestamp).format('YYYY-MM-DD HH:mm A')}</div>
				</div>
				<Switch
					checked={switchEnabled}
					onChange={onSwitchChange}
					checkedChildren="Hide Historical Data"
					unCheckedChildren="Show Historical Data"
					size="default"
					disabled={!hasParameters}
				/>
			</div>
		</div>
	);
}

export default InteractiveChartHeader;
